// Alternatives Tab — Phase 2 FR-2.3
// Alternatives discovery table with expandable rows, summary metrics, and top recommendations
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  LinearProgress,
  Chip,
  Grid,
  Skeleton,
  Alert,
  alpha,
  Tooltip,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  CompareArrows as AlternativesIcon,
  ArrowForward as ArrowIcon,
  EmojiEvents as TopPickIcon,
} from '@mui/icons-material';
import {
  useBomTopRecommendations,
} from '../../../hooks/useBomIntelligence';
import { colors } from '../../../theme/analyticsColors';
import { getConfidenceColor, getScoreColor } from '../utils/colors';
import type {
  BomAlternativesResponse,
  AlternativesLineItem,
  AlternativeMatch,
} from '../../../types';

interface AlternativesTabProps {
  bomId: string;
  alternativesData: BomAlternativesResponse;
  onViewPart?: (partId: string) => void;
}

const formatCurrency = (value: number | undefined | null) => {
  if (value == null) return '\u2014';
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : value > 0 ? '+' : '';
  if (abs < 0.01 && abs !== 0) return `${sign}$${parseFloat(abs.toPrecision(3))}`;
  return `${sign}$${abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPercent = (value: number | undefined | null) => {
  if (value == null) return '\u2014';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

const getSourceColor = (source: string) => {
  switch (source) {
    case 'CROSS_REFERENCE': return colors.chart.primary;
    case 'FFF_MATCH': return colors.chart.purple;
    case 'SIMILARITY': return colors.chart.cyan;
    default: return colors.secondary;
  }
};

const getSourceLabel = (source: string) => {
  switch (source) {
    case 'CROSS_REFERENCE': return 'Cross-Ref';
    case 'FFF_MATCH': return 'FFF Match';
    case 'SIMILARITY': return 'Similarity';
    default: return source;
  }
};

const getMatchTypeColor = (matchType: string) => {
  switch (matchType) {
    case 'EXACT_REPLACEMENT': return colors.success;
    case 'FUNCTIONAL_EQUIVALENT': return colors.chart.primary;
    case 'UPGRADE': return colors.chart.purple;
    case 'DOWNGRADE': return colors.warning;
    case 'ALTERNATE':
    case 'ALTERNATIVE': return colors.secondary;
    default: return colors.secondary;
  }
};

const getConfidenceLevelColor = (level: string) => {
  switch (level) {
    case 'HIGH': return colors.success;
    case 'MEDIUM': return colors.warning;
    case 'LOW': return colors.error;
    default: return colors.secondary;
  }
};

const AlternativesTab: React.FC<AlternativesTabProps> = ({ bomId, alternativesData, onViewPart }) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const { data: recommendations, isLoading: recsLoading } = useBomTopRecommendations(bomId);

  const toggleRow = (lineItemId: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(lineItemId)) {
        next.delete(lineItemId);
      } else {
        next.add(lineItemId);
      }
      return next;
    });
  };

  const { rollup, lineItemAlternatives } = alternativesData;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Summary Metrics */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2 }}>
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Typography sx={{ fontSize: '0.75rem', color: colors.secondary, fontWeight: 500 }}>
                Items with Alternatives
              </Typography>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: colors.primary }}>
                {rollup.itemsWithAlternatives}
                <Typography component="span" sx={{ fontSize: '0.875rem', color: colors.light, ml: 0.5 }}>
                  / {rollup.totalLineItems}
                </Typography>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2 }}>
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Typography sx={{ fontSize: '0.75rem', color: colors.secondary, fontWeight: 500 }}>
                Total Alternatives Found
              </Typography>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: colors.primary }}>
                {rollup.totalAlternativesFound}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2 }}>
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Typography sx={{ fontSize: '0.75rem', color: colors.secondary, fontWeight: 500 }}>
                Avg FFF Score
              </Typography>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: colors.primary }}>
                {(rollup.averageFFFScore * 100).toFixed(0)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2 }}>
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Typography sx={{ fontSize: '0.75rem', color: colors.secondary, fontWeight: 500 }}>
                Potential Savings
              </Typography>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: colors.success }}>
                ${rollup.totalPotentialSavings.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alternatives Table */}
      <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2 }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${colors.border}` }}>
            <Typography sx={{ fontWeight: 600, color: colors.primary, fontSize: '0.95rem' }}>
              Line Item Alternatives
            </Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40, bgcolor: colors.surfaceAlt }} />
                  <TableCell sx={{ fontWeight: 600, bgcolor: colors.surfaceAlt, fontSize: '0.75rem', color: colors.secondary }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: colors.surfaceAlt, fontSize: '0.75rem', color: colors.secondary }}>Part Number</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: colors.surfaceAlt, fontSize: '0.75rem', color: colors.secondary }}>Description</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: colors.surfaceAlt, fontSize: '0.75rem', color: colors.secondary }}># Alts</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: colors.surfaceAlt, fontSize: '0.75rem', color: colors.secondary }}>Top Alternative</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, bgcolor: colors.surfaceAlt, fontSize: '0.75rem', color: colors.secondary }}>Savings</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: colors.surfaceAlt, fontSize: '0.75rem', color: colors.secondary }}>FFF Score</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: colors.surfaceAlt, fontSize: '0.75rem', color: colors.secondary }}>Confidence</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItemAlternatives.map((item: AlternativesLineItem, idx: number) => (
                  <React.Fragment key={item.lineItemId}>
                    {/* Main row */}
                    <TableRow
                      hover
                      sx={{
                        bgcolor: idx % 2 === 0 ? 'transparent' : alpha(colors.surfaceAlt, 0.5),
                        cursor: item.alternativeCount > 0 ? 'pointer' : undefined,
                      }}
                      onClick={() => item.alternativeCount > 0 && toggleRow(item.lineItemId)}
                    >
                      <TableCell sx={{ py: 0.5 }}>
                        {item.alternativeCount > 0 && (
                          <IconButton size="small">
                            {expandedRows.has(item.lineItemId) ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', color: colors.secondary }}>{item.lineNumber}</TableCell>
                      <TableCell
                        sx={{
                          ...(onViewPart && item.partId && {
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' },
                          }),
                        }}
                        onClick={(e) => {
                          if (onViewPart && item.partId) {
                            e.stopPropagation();
                            onViewPart(item.partId);
                          }
                        }}
                      >
                        <Typography sx={{
                          fontFamily: 'JetBrains Mono, monospace',
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          color: onViewPart && item.partId ? colors.chart.primary : undefined,
                        }}>
                          {item.partNumber}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.partDescription || '\u2014'}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={item.alternativeCount}
                          size="small"
                          sx={{
                            minWidth: 32,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            bgcolor: item.alternativeCount > 0 ? alpha(colors.chart.primary, 0.1) : colors.surfaceAlt,
                            color: item.alternativeCount > 0 ? colors.chart.primary : colors.light,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        {item.topAlternative ? (
                          <Typography sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem' }}>
                            {item.topAlternative.alternativePartNumber}
                          </Typography>
                        ) : '\u2014'}
                      </TableCell>
                      <TableCell align="right">
                        {item.bestSavings > 0 ? (
                          <Typography sx={{ fontSize: '0.8rem', color: colors.success, fontWeight: 500 }}>
                            ${item.bestSavings.toFixed(2)}
                          </Typography>
                        ) : '\u2014'}
                      </TableCell>
                      <TableCell align="center">
                        {item.averageFFFScore > 0 ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                            <LinearProgress
                              variant="determinate"
                              value={item.averageFFFScore * 100}
                              sx={{
                                width: 40,
                                height: 6,
                                borderRadius: 3,
                                bgcolor: colors.border,
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: item.averageFFFScore >= 0.85 ? colors.success : item.averageFFFScore >= 0.70 ? colors.warning : colors.error,
                                  borderRadius: 3,
                                },
                              }}
                            />
                            <Typography sx={{ fontSize: '0.7rem', color: colors.secondary }}>
                              {(item.averageFFFScore * 100).toFixed(0)}%
                            </Typography>
                          </Box>
                        ) : '\u2014'}
                      </TableCell>
                      <TableCell align="center">
                        {item.topAlternative ? (
                          <Chip
                            label={item.topAlternative.confidenceLevel}
                            size="small"
                            sx={{
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              bgcolor: alpha(getConfidenceLevelColor(item.topAlternative.confidenceLevel), 0.1),
                              color: getConfidenceLevelColor(item.topAlternative.confidenceLevel),
                            }}
                          />
                        ) : '\u2014'}
                      </TableCell>
                    </TableRow>

                    {/* Expanded sub-table */}
                    {item.alternativeCount > 0 && (
                      <TableRow>
                        <TableCell colSpan={9} sx={{ py: 0, px: 0, border: 'none' }}>
                          <Collapse in={expandedRows.has(item.lineItemId)} timeout="auto" unmountOnExit>
                            <Box sx={{ mx: 3, my: 1.5, p: 1.5, bgcolor: colors.surfaceAlt, borderRadius: 1.5, border: `1px solid ${colors.border}` }}>
                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.primary, mb: 1 }}>
                                Alternatives for {item.partNumber}
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, color: colors.secondary, py: 0.5 }}>Part Number</TableCell>
                                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, color: colors.secondary, py: 0.5 }}>Description</TableCell>
                                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, color: colors.secondary, py: 0.5 }}>Match Type</TableCell>
                                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, color: colors.secondary, py: 0.5 }}>Source</TableCell>
                                    <TableCell align="center" sx={{ fontSize: '0.7rem', fontWeight: 600, color: colors.secondary, py: 0.5 }}>Score Breakdown</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 600, color: colors.secondary, py: 0.5 }}>Cost Diff</TableCell>
                                    <TableCell align="center" sx={{ fontSize: '0.7rem', fontWeight: 600, color: colors.secondary, py: 0.5 }}>Composite</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {item.alternatives.map((alt: AlternativeMatch) => (
                                    <TableRow key={alt.alternativePartId} hover>
                                      <TableCell
                                        sx={{
                                          py: 0.5,
                                          ...(onViewPart && alt.alternativePartId && {
                                            cursor: 'pointer',
                                            '&:hover': { textDecoration: 'underline' },
                                          }),
                                        }}
                                        onClick={() => onViewPart && alt.alternativePartId && onViewPart(alt.alternativePartId)}
                                      >
                                        <Typography sx={{
                                          fontFamily: 'JetBrains Mono, monospace',
                                          fontSize: '0.75rem',
                                          fontWeight: 500,
                                          color: onViewPart && alt.alternativePartId ? colors.chart.primary : undefined,
                                        }}>
                                          {alt.alternativePartNumber}
                                        </Typography>
                                        {alt.manufacturerName && (
                                          <Typography sx={{ fontSize: '0.65rem', color: colors.light }}>
                                            {alt.manufacturerName}
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell sx={{ fontSize: '0.75rem', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', py: 0.5 }}>
                                        {alt.alternativeDescription || '\u2014'}
                                      </TableCell>
                                      <TableCell sx={{ py: 0.5 }}>
                                        <Chip
                                          label={alt.matchType.replace(/_/g, ' ')}
                                          size="small"
                                          sx={{
                                            fontSize: '0.6rem',
                                            fontWeight: 600,
                                            bgcolor: alpha(getMatchTypeColor(alt.matchType), 0.1),
                                            color: getMatchTypeColor(alt.matchType),
                                            height: 20,
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ py: 0.5 }}>
                                        <Chip
                                          label={getSourceLabel(alt.source)}
                                          size="small"
                                          sx={{
                                            fontSize: '0.6rem',
                                            fontWeight: 500,
                                            bgcolor: alpha(getSourceColor(alt.source), 0.1),
                                            color: getSourceColor(alt.source),
                                            height: 20,
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell align="center" sx={{ py: 0.5 }}>
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
                                          {[
                                            { abbr: 'C1', tooltip: 'Category 1 (Identity / Function)', value: alt.functionScore },
                                            { abbr: 'C2', tooltip: 'Category 2 (Performance / Fit)', value: alt.fitScore },
                                            { abbr: 'C3', tooltip: 'Category 3+ (Processing / Form)', value: alt.formScore },
                                          ].map((dim, i) => (
                                            <Tooltip key={i} title={`${dim.tooltip}: ${(dim.value * 100).toFixed(0)}%`} arrow>
                                              <Box sx={{ textAlign: 'center' }}>
                                                <LinearProgress
                                                  variant="determinate"
                                                  value={dim.value * 100}
                                                  sx={{
                                                    width: 24,
                                                    height: 4,
                                                    borderRadius: 2,
                                                    bgcolor: colors.border,
                                                    '& .MuiLinearProgress-bar': {
                                                      bgcolor: dim.value >= 0.8 ? colors.success : dim.value >= 0.6 ? colors.warning : colors.error,
                                                      borderRadius: 2,
                                                    },
                                                  }}
                                                />
                                              </Box>
                                            </Tooltip>
                                          ))}
                                          <Typography sx={{ fontSize: '0.65rem', color: colors.secondary, ml: 0.5 }}>
                                            {(alt.fffScore * 100).toFixed(0)}%
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell align="right" sx={{ py: 0.5 }}>
                                        {alt.costDifference != null ? (
                                          <Typography sx={{
                                            fontSize: '0.75rem',
                                            fontWeight: 500,
                                            color: alt.costDifference < 0 ? colors.success : alt.costDifference > 0 ? colors.error : colors.secondary,
                                          }}>
                                            {formatCurrency(alt.costDifference)}
                                            {alt.costDifferencePercent != null && (
                                              <Typography component="span" sx={{ fontSize: '0.65rem', color: colors.light, ml: 0.5 }}>
                                                ({formatPercent(alt.costDifferencePercent)})
                                              </Typography>
                                            )}
                                          </Typography>
                                        ) : '\u2014'}
                                      </TableCell>
                                      <TableCell align="center" sx={{ py: 0.5 }}>
                                        <Chip
                                          label={(alt.compositeScore * 100).toFixed(0)}
                                          size="small"
                                          sx={{
                                            fontSize: '0.7rem',
                                            fontWeight: 700,
                                            minWidth: 36,
                                            height: 22,
                                            bgcolor: alpha(getScoreColor(alt.compositeScore * 100, { low: 50, mid: 70 }), 0.1),
                                            color: getScoreColor(alt.compositeScore * 100, { low: 50, mid: 70 }),
                                          }}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Top Recommendations */}
      <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TopPickIcon sx={{ fontSize: 20, color: colors.chart.primary }} />
            <Typography sx={{ fontWeight: 600, color: colors.primary, fontSize: '0.95rem' }}>
              Top Recommendations
            </Typography>
          </Box>
          {recsLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[1, 2, 3].map(i => <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: 1 }} />)}
            </Box>
          ) : recommendations && recommendations.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {recommendations.slice(0, 8).map((rec, idx) => (
                <Box
                  key={`${rec.lineItemId}-${rec.recommendedPartId}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: idx === 0 ? alpha(colors.chart.primary, 0.06) : colors.surfaceAlt,
                    border: idx === 0 ? '1px solid' : `1px solid ${colors.border}`,
                    borderColor: idx === 0 ? alpha(colors.chart.primary, 0.3) : colors.border,
                  }}
                >
                  <Chip
                    label={`#${idx + 1}`}
                    size="small"
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      minWidth: 32,
                      bgcolor: idx === 0 ? colors.chart.primary : colors.border,
                      color: idx === 0 ? colors.surface : colors.primary,
                    }}
                  />
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem', fontWeight: 500, color: colors.primary }}>
                      {rec.originalPartNumber}
                    </Typography>
                    <ArrowIcon sx={{ fontSize: 16, color: colors.light }} />
                    <Typography sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem', fontWeight: 600, color: colors.primary }}>
                      {rec.recommendedPartNumber}
                    </Typography>
                  </Box>
                  <Chip
                    label={rec.matchType.replace(/_/g, ' ')}
                    size="small"
                    sx={{
                      fontSize: '0.6rem',
                      fontWeight: 600,
                      bgcolor: alpha(getMatchTypeColor(rec.matchType), 0.1),
                      color: getMatchTypeColor(rec.matchType),
                      height: 20,
                    }}
                  />
                  {rec.costDifference != null && rec.costDifference < 0 && (
                    <Chip
                      label={`Save $${Math.abs(rec.costDifference).toFixed(2)}`}
                      size="small"
                      sx={{
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        bgcolor: alpha(colors.success, 0.1),
                        color: colors.success,
                        height: 20,
                      }}
                    />
                  )}
                  <Chip
                    label={`Score: ${(rec.compositeScore * 100).toFixed(0)}`}
                    size="small"
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      bgcolor: alpha(colors.chart.primary, 0.1),
                      color: colors.chart.primary,
                      height: 20,
                    }}
                  />
                </Box>
              ))}
            </Box>
          ) : (
            <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
              No recommendations available yet. Run alternatives discovery to find potential substitutes.
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AlternativesTab;
